import type { SocialPlatform } from '@pulsify/shared/dto'
import { CircleHelp } from 'lucide-react'
import { PLATFORM_UI } from '~/features/crossposting/lib/platforms'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/shared/ui/dialog'

function SectionHeading({ platform }: { platform: SocialPlatform }) {
  const ui = PLATFORM_UI[platform]
  const Icon = ui.icon
  return (
    <div className="flex items-center gap-2">
      <span className="flex h-7 w-7 items-center justify-center rounded-md" style={ui.brandStyle}>
        <Icon className="h-4 w-4" />
      </span>
      <h3 className="font-semibold">{ui.label()}</h3>
    </div>
  )
}

function FacebookHelp() {
  return (
    <section className="space-y-3">
      <SectionHeading platform="facebook" />
      <p className="text-muted-foreground text-sm">{m.crosspostingHelpFbLead()}</p>
      <ul className="list-disc space-y-1.5 pl-4 text-sm">
        <li>{m.crosspostingHelpFbReq1()}</li>
        <li>{m.crosspostingHelpFbReq2()}</li>
        <li>{m.crosspostingHelpFbReq3()}</li>
      </ul>
      <div className="rounded-lg border bg-muted/40 p-3 text-muted-foreground text-sm">
        {m.crosspostingHelpFbWhyPerson()}
      </div>
      <div className="space-y-2 rounded-lg border p-3">
        <p className="font-medium text-sm">{m.crosspostingHelpFixTitle()}</p>
        <p className="text-muted-foreground text-sm">{m.crosspostingHelpFbLinkedButNoPage()}</p>
        <ol className="list-decimal space-y-1 pl-4 text-muted-foreground text-sm">
          <li>{m.crosspostingHelpFixStep1()}</li>
          <li>{m.crosspostingHelpFixStep2()}</li>
          <li>{m.crosspostingHelpFixStep3()}</li>
        </ol>
      </div>
    </section>
  )
}

function InstagramHelp() {
  return (
    <section className="space-y-3">
      <SectionHeading platform="instagram" />
      <p className="text-muted-foreground text-sm">{m.crosspostingHelpIgLead()}</p>
      <ul className="list-disc space-y-1.5 pl-4 text-sm">
        <li>{m.crosspostingHelpIgReq1()}</li>
        <li>{m.crosspostingHelpIgReq2()}</li>
        <li>{m.crosspostingHelpIgReq3()}</li>
        <li>{m.crosspostingHelpIgReq4()}</li>
      </ul>
    </section>
  )
}

export function ConnectHelpButton({
  platform,
  className,
}: {
  platform?: SocialPlatform
  className?: string
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className={cn('gap-1.5', className)}>
          <CircleHelp className="h-4 w-4" />
          {m.crosspostingHelp()}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{m.crosspostingHelpTitle()}</DialogTitle>
          <DialogDescription>{m.crosspostingHelpDescription()}</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {(!platform || platform === 'facebook') && <FacebookHelp />}
          {(!platform || platform === 'instagram') && <InstagramHelp />}
          <p className="text-muted-foreground text-xs">{m.crosspostingHelpMetaNote()}</p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
